import { useState } from 'react';
import type { QueueOutcome } from '../lib/api';

/**
 * Confirmation for a change past the large-change threshold, before it is
 * staged.
 *
 * A normal edit never reaches this dialog at all — it stages silently, which
 * is the entire point of a queue instead of publishing on every keystroke.
 * This only exists for the one case worth a second look: a jump big enough to
 * usually be a mistyped decimal point. Confirming stages the change; it still
 * does not go live until the queue is synced.
 *
 * The threshold check lives on the server; this only surfaces it. A client-side
 * guard would be trivially bypassed.
 */

interface Props {
  channelName: string;
  productTitle: string;
  field: 'price' | 'title' | 'sku';
  oldValue: string;
  newValue: string;
  percentChange: number;
  /**
   * Set only for a price change on a channel where this listing already
   * carries a business price — what it will also be set to. Null everywhere
   * else, including every non-Amazon channel.
   */
  businessPrice?: { from: string; to: string; label: string } | null;
  onCancel: () => void;
  onConfirm: () => Promise<QueueOutcome>;
  onStaged: (outcome: QueueOutcome) => void;
}

export function ConfirmWriteDialog({
  channelName, productTitle, field, oldValue, newValue, percentChange, businessPrice, onCancel, onConfirm, onStaged,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onStaged(await onConfirm());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onClick={busy ? undefined : onCancel}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Confirm a large change</h3>

        <p className="muted small" style={{ margin: 0 }}>{productTitle}</p>

        <div className="diff">
          <span className="from">{oldValue}</span>
          <span aria-hidden>→</span>
          <span className="to">{newValue}</span>
        </div>

        <div className="alert warn">
          This changes the {field === 'sku' ? 'SKU' : field} by <strong>{percentChange}%</strong> on{' '}
          <strong>{channelName}</strong>. Large jumps are usually a mistyped decimal point. Confirming
          stages it — it will not go live until the queue is synced.
        </div>

        {businessPrice && (
          <p className="small muted" style={{ margin: 0 }}>
            The Amazon Business price will also change from <strong>{businessPrice.from}</strong> to{' '}
            <strong>{businessPrice.to}</strong> ({businessPrice.label}).
          </p>
        )}

        {error && <div className="alert error">{error}</div>}

        <div className="actions">
          <button onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="danger" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Staging…' : `Yes, stage a ${percentChange}% change`}
          </button>
        </div>
      </div>
    </div>
  );
}
